<template>
  <div>
    <page-content-toolbar
      :title="$t('sidenav.projects')"
      :hide-project-name="true"
    >
    </page-content-toolbar>

    <loader v-if="loading"
      :label="$t('project.searchingProjects')"
    />
    <div class="ma-3" v-else>
      <v-data-iterator
        v-if="projects.length > 0 || !!searchProjectsQuery  || searching"
        :headers="headers"
        :items="projects"
        :options.sync="options"
        :server-items-length="totalProjects"
        :loading="searching"
        :footer-props="footerProps"
        :hide-default-footer="projects.length === 0"
        no-data-text=""
        :loading-text="$t('project.searchingProjects')"
        class="rai-projects-table"
        @click:row="goToProject($event)"
      >
      <template v-slot:header>
        <v-toolbar
          class="my-1 elevation-0 raison-highlight"
        >
          <v-btn
            @click="openAddDialog"
            class="raison-btn"
          >
            <v-icon >mdi-plus</v-icon>
            {{ $t('project.create') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="searchProjectsQuery"
            clearable
            flat
            hide-details
            prepend-inner-icon="mdi-magnify"
            color="grey"
            :label="$t('global.searchProjects')"
          ></v-text-field>
        </v-toolbar>
      </template>

      <template v-slot:default="props">
        <v-row>
          <v-col
            v-for="(item, index) in props.items"
            :key="index"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card class="rai-project">
              <v-card-title class="subheading font-weight-bold">
                {{ item.name }}
                <v-btn
                  v-if="isOwner(item.id)"
                  icon
                  @click.stop="openEditDialog(item)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-subtitle class="caption">{{ new Date(item.lastModifiedDate).toLocaleString($i18n.locale) }}</v-card-subtitle>
              <v-card-text class="ma-0 rai-project">
                <p v-if="item.description" class="rai-description">
                  {{ item.description }}
                </p>
                <a
                  v-if="item.description && item.description.length > 100"
                  href="#"
                  @click.stop="openEditDialog(item)"
                >
                  {{ $t('global.more') }}
                </a>
              </v-card-text>
              <v-card-actions>
                <v-tooltip bottom v-if="isEditor(item.id)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      @click.stop="goToEditor(item)"
                      class="raison-btn elevation-2"
                      icon
                      x-large
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-clipboard-edit-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('project.edit') }}</span>
                </v-tooltip>
                <v-spacer></v-spacer>
                <v-tooltip bottom v-if="isOwner(item.id)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      @click.stop="shareDialog(item)"
                      class="raison-btn elevation-2"
                      icon
                      x-large
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-share-variant</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('share.share') }}</span>
                </v-tooltip>
                <v-tooltip bottom v-if="isRunner(item.id)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      @click.stop="goToPlay(item)"
                      class="raison-btn elevation-2"
                      icon
                      x-large
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-play-box-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('sidenav.play') }}</span>
                </v-tooltip>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </template>
      </v-data-iterator>
      <v-container fluid v-else>
        <v-row no-gutters>
          <v-col align="center">
            <p class="text-h2">
              {{ $t('project.welcome') }}
            </p>
            <p class="text-h2">{{  userInfo ? userInfo.name : '' }}</p>
            <v-btn
              @click="switchHelpDrawer"
              class="raison-btn"
            >
              {{ $t('help.whatINeedToKnowBeforeStarting') }}
            </v-btn>
            <p class="text-h1">
              {{ $t('project.letsStartByCreatingANewProject') }}
            </p>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col align="center">
            <v-btn
              @click="openAddDialog"
              class="raison-btn"
            >
              <v-icon>mdi-plus</v-icon>
              {{ $t('project.create') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <add-project-dialog
      :open.sync="openDialog"
      :validate-function="dialogFunction"
      :prop-project="project"
      :edit="editDialog"
      :readonly="readonlyProjectDialog"
      @cancel-edition="openDialog = false"
      @project-deleted="onProjectDeleted"
    />

    <share-project-dialog
      :open.sync="openShareDialog"
      :project="project"
      @raison-event="$emit('raison-event', $event)"
    />
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import AddProjectDialog from '@/components/dashboard/AddProjectDialog';
import ShareProjectDialog from '@/components/dashboard//ShareProjectDialog';
import Loader from '@/components/global/Loader'

import helpService from '@/services/help.js'
import projectService from '@/services/projects.js';
import userRightMixin from '@/mixins/user-rights-mixin.js';

export default {
  name: 'Dashboard',

  components: { AddProjectDialog, Loader, PageContentToolbar, ShareProjectDialog },

  mixins: [ userRightMixin ],

  data () {
    return {
      projects: [],
      totalProjects: 0,
      loading: false,
      options: { itemsPerPage: 8, page: 1, sortBy: ['lastModifiedDate'], sortDesc: [true]},
      firstLoad: true,
      openDialog: false,
      openShareDialog: false,
      dialogFunction: () => {},
      editDialog: false,
      project: {},
      readonlyProjectDialog: false,
      searchProjectsQuery: '',
      searching: false,
    };
  },

  computed: {
    headers () {
      return [
        { text: '', value: 'actions', width: '34px', sortable: false },
        { text: this.$i18n.t('project.projectName'), value: 'name' },
        { text: this.$i18n.t('global.lastModifiedDate'), value: 'lastModifiedDate' },
      ];
    },
    footerProps () {
      return {
        'items-per-page-options': [8, 16, 32],
        'items-per-page-text': this.$i18n.t('global.rowPerPage'),
        'page-text': '{0}-{1} / {2}',
      };
    },
  },

  methods: {

    onProjectDeleted (event) {
      this.projects = this.projects.filter(project => project.id !== event.projectId);
      this.project = {}
    },

    goToEditor (item) {
      this.$router.push({ name: 'editor', params: { id: item.id} });
    },

    goToPlay (item) {
      this.$router.push({ name: 'play', params: { id: item.id} });
    },

    openAddDialog () {
      this.dialogFunction = this.addProject;
      this.editDialog = false;
      this.readonlyProjectDialog = false;
      this.project = {};
      this.openDialog = true;
    },

    openEditDialog (project) {
      this.dialogFunction = this.editProject;
      this.editDialog = true;
      this.project = project;
      this.readonlyProjectDialog = !this.isOwner(project.id);
      this.openDialog = true;
    },

    shareDialog (project) {
      this.project = project;
      this.openShareDialog = true;
    },

    addProject (project) {
      return new Promise((resolve, reject) => {
        projectService.createProject(project).then(res => {
          // console.log('createProject:', res);
          // console.log('projects:', this.projects);
          this.userInfo.authorities.push(`OWNER:${res.data.id}`)
          this.projects.splice(0,0,res.data);
          this.totalProjects++;
          this.openDialog = false;
          this.$emit('raison-event', { type: 'success', messages: [this.$t('project.created')] });
          resolve();
        })
        .catch(err => {
          console.log(err);
          this.openDialog = false;
          this.$emit('raison-event', { type: 'error', messages: [this.$t('project.createError')] });
          reject();
        });
      });
    },

    editProject (project) {
      return new Promise((resolve, reject) => {
        projectService.updateProject(project).then(res => {
          if (!res.data.id) {
            throw 'Api error';
          }
          const projIndex = this.projects.findIndex(pro => project.id === pro.id);
          this.projects.splice(projIndex, 1, res.data);
          this.openDialog = false;
          this.$emit('raison-event', { type: 'success', messages: [this.$t('project.edited')] });
          resolve();
        }).catch(err => {
          this.openDialog = false;
          this.$emit('raison-event', { type: 'error', messages: [this.$t('project.editError'), err] });
          reject();
        });
      });
    },

    getProjects () {
      this.searching = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      projectService.getProjects(sortBy, sortDesc, page ? page - 1 : undefined, itemsPerPage, this.searchProjectsQuery).then(res => {
        this.projects = res.data.content;
        this.totalProjects = res.data.page.totalElements;
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages:[err] });
      }).finally(() => {
        this.searching = false;
        this.loading = false;
      })
    },

    switchHelpDrawer() {
      helpService.setHelpPage(this.$store, 'what_i_need_to_know')
      helpService.switchHelpDrawer(this.$store)
    },
  },

  created () {
    this.$store.dispatch('unsetProjectId')
    this.searchProjectsQuery = this.$route.params.query;
    this.getUserInfo();
    helpService.setHelpPage(this.$store, 'what_i_need_to_know')
  },

  watch: {
    searchProjectsQuery() {
      this.options.page = 1
      this.options.numberOfPages = 1
      // console.log('searchProjectsQuery')
      this.getProjects();
    },
    options: {
      handler () {
        if (this.firstLoad) {
          this.firstLoad = false;
        } else {
          // console.log('options')
          this.getProjects();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.rai-project {
  min-height: 104px;
}
.rai-description {
  overflow: hidden;
  /* width:100px; */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  margin: 0px;
}
</style>
